/**
 * All site copy, extracted from the Quikit website export (quikit-website.html).
 * Edit content here — sections only handle layout and motion.
 */

/**
 * The canonical origin for this build, used for canonical URLs, OG images and the links
 * in lead notification emails. Production is quikit.ai; the UAT build sets
 * NEXT_PUBLIC_SITE_URL to https://uat.quikit.ai so nothing points at the live site.
 */
export const SITE = process.env.NEXT_PUBLIC_SITE_URL ?? "https://quikit.ai";
/**
 * Where "Log in" and "Start free" send people: the live sign-in. Set NEXT_PUBLIC_AUTH_URL only
 * to point a build somewhere else on purpose.
 */
export const AUTH = process.env.NEXT_PUBLIC_AUTH_URL || "https://authn.quikit.ai";
export const LINKS = {
  register: `${AUTH}/register`,
  login: `${AUTH}/login`,
  demo: "#contact",
  privacy: "/legal/privacy",
  terms: "/legal/terms",
  security: "/legal/security",
};

/* ------------------------------------------------------------------ */
/*  Product catalogue                                                  */
/* ------------------------------------------------------------------ */
export type Family =
  | "Platform & Technology"
  | "Goals & Performance"
  | "People & HR"
  | "Work & Projects"
  | "Sales & Customer Success"
  | "Marketing"
  | "QuikOffice"
  | "Customer support";

export const FAMILIES: Record<Family, string> = {
  "Platform & Technology": "Industry depth, automation and the tools to build on the Quikit platform.",
  "Goals & Performance": "Connect strategy, goals and analytics to day-to-day work.",
  "People & HR": "Hire, manage and develop your workforce on one connected record.",
  "Work & Projects": "Plan and deliver work, with budgets, purchasing and stock connected.",
  "Sales & Customer Success": "Win deals and keep every customer relationship in one place.",
  Marketing: "Plan, publish and grow visibility across social, search and email.",
  QuikOffice: "Docs, sheets, slides, forms and e-signatures — free with any product.",
  "Customer support": "Resolve every request with the full customer record in view.",
};

export type Product = {
  key: string;
  name: string;
  family: Family;
  desc: string;
  status: "live" | "soon";
  price: number | null; // USD per month
  unit: "user" | "month" | "brand" | "free" | "custom" | null;
};

/** Ordered by family, and within a family in catalogue order — the nav and footer read from this. */
export const PRODUCTS: Product[] = [
  { key: "projects", name: "QuikProjects", family: "Work & Projects", desc: "Plan, build, test and ship software from one AI-first delivery platform.", status: "live", price: 1, unit: "user" },
  { key: "events", name: "QuikEvents", family: "Work & Projects", desc: "Plan and run company events, from invitations through to attendance.", status: "soon", price: null, unit: null },
  { key: "finance", name: "QuikFinance", family: "Work & Projects", desc: "Accounting, GST, banking and reporting connected to every transaction.", status: "live", price: 1, unit: "user" },
  { key: "purchase", name: "QuikPurchase", family: "Work & Projects", desc: "Requests, approvals, vendors and purchase orders in one controlled flow.", status: "live", price: 49.99, unit: "month" },
  { key: "inventory", name: "QuikInventory", family: "Work & Projects", desc: "Track stock, movements and valuation alongside purchasing and finance.", status: "soon", price: null, unit: null },

  { key: "scale", name: "QuikScale", family: "Goals & Performance", desc: "OKRs, KPIs and execution rhythms that turn strategy into results.", status: "live", price: 4.99, unit: "user" },
  { key: "insights", name: "QuikInsights", family: "Goals & Performance", desc: "Unify marketing data from every channel into reports that drive decisions.", status: "live", price: 19.99, unit: "month" },

  { key: "people", name: "QuikPeople", family: "People & HR", desc: "Hire, onboard and manage your workforce in an HRMS that understands your people.", status: "live", price: 1, unit: "user" },
  { key: "chat", name: "QuikChat", family: "People & HR", desc: "Team messaging, meetings and files — connected to the work they’re about.", status: "live", price: 1, unit: "user" },
  { key: "skill", name: "QuikSkill", family: "People & HR", desc: "Courses, certifications and AI-proctored assessments that build real skills.", status: "live", price: 1, unit: "user" },
  { key: "attendance", name: "QuikAttendance", family: "People & HR", desc: "Attendance, shifts and time tracking — with a live view of who’s actually available.", status: "live", price: 1, unit: "user" },
  { key: "recruitment", name: "QuikRecruitment", family: "People & HR", desc: "Source, screen and hire candidates straight into your people record.", status: "soon", price: null, unit: null },

  { key: "sales", name: "QuikSales", family: "Sales & Customer Success", desc: "Lead and opportunity management, sales enablement and pipeline visibility.", status: "soon", price: 4.99, unit: "user" },
  { key: "crm", name: "QuikCRM", family: "Sales & Customer Success", desc: "Manage leads, customers and pipeline with AI-assisted relationship management.", status: "live", price: 1, unit: "user" },

  { key: "social", name: "QuikProSocial", family: "Marketing", desc: "Plan, create and publish a LinkedIn strategy that builds pipeline.", status: "live", price: 49.99, unit: "brand" },
  { key: "mail", name: "QuikMail", family: "Marketing", desc: "Personalised email campaigns, connected to the rest of your customer data.", status: "soon", price: null, unit: null },
  { key: "seo", name: "QuikSEO", family: "Marketing", desc: "An AI-powered SEO team that audits, prioritises and executes — including AEO and GEO.", status: "live", price: 99.99, unit: "brand" },

  { key: "forms", name: "QuikForms", family: "QuikOffice", desc: "Build forms and surveys, collect responses and put answers to work.", status: "live", price: 0, unit: "free" },
  { key: "docs", name: "QuikDocs", family: "QuikOffice", desc: "Documents and a searchable knowledge base, next to the work they describe.", status: "live", price: 0, unit: "free" },
  { key: "signs", name: "QuikSigns", family: "QuikOffice", desc: "Prepare, send, sign and track agreements without leaving Quikit.", status: "live", price: 0, unit: "free" },
  { key: "presentation", name: "QuikSlides", family: "QuikOffice", desc: "Build and present decks from your business data, without another subscription.", status: "live", price: 0, unit: "free" },
  { key: "sheets", name: "QuikSheets", family: "QuikOffice", desc: "Spreadsheets with formulas, charts and live collaboration — one sheet, one number.", status: "live", price: 0, unit: "free" },
  { key: "pdf", name: "QuikPdf", family: "QuikOffice", desc: "Read, edit, merge and split PDFs without reaching for another tool.", status: "soon", price: 0, unit: "free" },
  { key: "drive", name: "QuikDrive", family: "QuikOffice", desc: "Shared file storage that stays attached to the work it belongs to.", status: "soon", price: 0, unit: "free" },
  { key: "image", name: "QuikImage", family: "QuikOffice", desc: "Create and edit images for documents, decks and campaigns.", status: "soon", price: 0, unit: "free" },
  { key: "calendar", name: "QuikCalendar", family: "QuikOffice", desc: "Scheduling and availability shared across your whole team.", status: "soon", price: 0, unit: "free" },

  { key: "infra", name: "QuikInfra", family: "Platform & Technology", desc: "Run every stage of construction with AI-powered project intelligence.", status: "live", price: null, unit: "custom" },
  { key: "studio", name: "QuikStudio", family: "Platform & Technology", desc: "Build custom business applications on your Quikit data — no code required.", status: "soon", price: null, unit: null },
  { key: "flow", name: "QuikFlow", family: "Platform & Technology", desc: "Automate handoffs, approvals and routing across every Quikit app.", status: "soon", price: null, unit: null },
  { key: "builder", name: "QuikBuilder", family: "Platform & Technology", desc: "Assemble internal tools and interfaces on top of your Quikit data.", status: "soon", price: null, unit: null },
  { key: "asset", name: "QuikAsset", family: "Platform & Technology", desc: "Track every asset’s lifecycle and maintenance, from purchase to retirement.", status: "soon", price: 1, unit: "user" },
  { key: "core", name: "QuikCore", family: "Platform & Technology", desc: "The shared platform services — identity, permissions and data — every app runs on.", status: "soon", price: null, unit: null },
  { key: "vision", name: "QuikVision", family: "Platform & Technology", desc: "Turn business data into dashboards, forecasts and actionable insight.", status: "soon", price: null, unit: null },

  { key: "support", name: "QuikSupport", family: "Customer support", desc: "Ticketing, help desk and IT service management for every customer.", status: "soon", price: 4.99, unit: "user" },
];

export const productByKey = (key: string) => PRODUCTS.find((p) => p.key === key)!;
export const LIVE_COUNT = PRODUCTS.filter((p) => p.status === "live").length;

/** Whole dollars drop the cents, so $1 never prints as $1.00. */
export const usd = (n: number) => `$${n.toLocaleString("en-US", { maximumFractionDigits: 2 })}`;

export function priceLabel(p: Product) {
  if (p.unit === "custom") return "Custom";
  if (p.unit === "free") return "Free";
  if (p.price == null) return "At launch";
  const unit = p.unit === "user" ? "/ user / mo" : p.unit === "brand" ? "/ brand / mo" : "/ mo";
  return `${usd(p.price)} ${unit}`;
}

/* ------------------------------------------------------------------ */
/*  Hero                                                               */
/* ------------------------------------------------------------------ */
export const HERO = {
  pill: {
    tag: "Quikit",
    text: "The AI-first Work Operating System",
  },
  title: "AI that understands your business.\nSoftware that helps run it.",
  sub: "Quikit connects sales, projects, people, finance and support on one shared AI layer — so every team works from the same data, and every decision comes faster.",
  micro: ["No credit card required", "From $1 per user / month", "Cancel any time"],
};

/* ------------------------------------------------------------------ */
/*  Facts / problem / suite                                            */
/* ------------------------------------------------------------------ */
export const FACTS = [
  { value: PRODUCTS.length, prefix: "", suffix: "", label: "Business apps on one platform" },
  { value: LIVE_COUNT, prefix: "", suffix: "", label: "Ready to start today" },
  { value: 14, prefix: "", suffix: "", label: "Days free trial on every product" },
  { value: 1, prefix: "$", suffix: "", label: "Starting price per user / month" },
];

export const PROBLEM = {
  eyebrow: "The problem",
  title: "The cost isn’t the apps.\nIt’s the seams between them.",
  text: "Most businesses run on a patchwork of tools. Data gets trapped, work gets repeated and decisions wait for reports.",
  stats: [
    { value: 58, suffix: "%", text: "of the workday goes on “work about work” — searching, chasing and switching — not the work itself.", source: "Asana, Anatomy of Work 2023" },
    { value: 36, suffix: "%", text: "of organisations deliver their projects on time.", source: "Wellingtone, 2026" },
    { value: 4, suffix: " hrs", text: "lost each week just reorienting after switching between apps.", source: "Harvard Business Review, 2022" },
  ],
};

export const SUITE = {
  eyebrow: "The suite",
  title: "One suite for every team.",
  text: "Purpose-built apps that share one data layer and one AI. Start with the problem you need to solve.",
  cards: [
    { title: "Grow revenue", text: "Capture leads, manage pipeline and keep every customer relationship in view.", apps: ["crm", "sales"], icon: "chart" },
    { title: "Deliver projects", text: "Plan, build and ship work — with budgets and purchasing connected.", apps: ["projects", "finance", "purchase"], icon: "check" },
    { title: "Build your team", text: "Hire, onboard, track attendance and keep everyone in the conversation.", apps: ["people", "attendance", "chat"], icon: "user" },
    { title: "Market smarter", text: "Grow on LinkedIn, win in search and see which channels actually work.", apps: ["social", "seo", "insights"], icon: "megaphone" },
    { title: "Align strategy", text: "Turn goals into OKRs, track KPIs and see execution in one place.", apps: ["scale"], icon: "target" },
    { title: "Support customers", text: "Resolve every request faster with the full customer record in view.", apps: ["support"], icon: "chat" },
  ] as const,
};

/* ------------------------------------------------------------------ */
/*  Product tour                                                       */
/* ------------------------------------------------------------------ */
export type TourApp = {
  key: string;
  name: string;
  category: string;
  headline: string;
  sub: string;
  items: { name: string; desc: string }[];
};

export const TOUR: TourApp[] = [
  {
    key: "projects",
    name: "QuikProjects",
    category: "AI-first software delivery platform",
    headline: "Your whole software delivery stack. One platform.",
    sub: "Project management, documentation, time tracking, tasks, test management and MCP-powered AI in one workspace built for IT and software teams.",
    items: [
      { name: "Project management", desc: "Issues, epics, stories, backlogs, sprints, dependencies, releases, custom workflows and custom fields." },
      { name: "Scrum and Kanban", desc: "Run Scrum, Kanban or custom workflows with boards, swimlanes and statuses." },
      { name: "Time tracking", desc: "Log time against real work. Timesheets, approvals, resource planning and workload visibility included." },
    ],
  },
  {
    key: "crm",
    name: "QuikCRM",
    category: "AI-first CRM",
    headline: "A CRM that works for your sales team.",
    sub: "Leads, contacts, accounts, opportunities, pipelines and activities in one workspace — with AI that cuts CRM admin so reps spend more time closing business.",
    items: [
      { name: "Lead management", desc: "Capture, assign and qualify leads with source, ownership and status kept connected." },
      { name: "Opportunity management", desc: "Track deal value, stage, probability, activities and next steps through customisable pipelines." },
      { name: "Forecasting", desc: "Understand expected revenue and pipeline value from real opportunity movement." },
    ],
  },
  {
    key: "people",
    name: "QuikPeople",
    category: "AI-first HRMS",
    headline: "An HRMS that helps you understand your people.",
    sub: "Recruitment, onboarding, core HR, attendance, leave, performance and workflows in one connected workspace — with AI that cuts repetitive HR work and surfaces workforce patterns.",
    items: [
      { name: "Recruitment management", desc: "Openings, applications, screening, interview stages and hiring decisions in one pipeline." },
      { name: "Core HR", desc: "Profiles, departments, designations, reporting managers, locations and employment history in one system." },
      { name: "Performance management", desc: "Goals, KPIs, feedback, reviews and history for a continuous view of growth." },
    ],
  },
  {
    key: "finance",
    name: "QuikFinance",
    category: "Connected finance and accounting",
    headline: "Every transaction connected. Every number trusted.",
    sub: "Sales, purchases, inventory, accounting, payments, projects and reporting in one platform. Built for Indian businesses, with double-entry accounting and financial controls at the core.",
    items: [
      { name: "Sales and receivables", desc: "Quotations, orders, invoices, recurring invoices, credit notes and payments, with a live receivables dashboard." },
      { name: "Core accounting", desc: "Chart of accounts, journals, fixed assets, budgets, cost dimensions, multi-currency and FX revaluation." },
      { name: "19 standard reports", desc: "P&L, balance sheet, cash flow, trial balance, ageing, stock valuation, budget vs actual and more." },
    ],
  },
  {
    key: "insights",
    name: "QuikInsights",
    category: "AI marketing analytics",
    headline: "Turn marketing data into better decisions.",
    sub: "Paid, organic, email and CRM data in one live view. AI explains what's working, what isn't, and what to do next.",
    items: [
      { name: "Unified growth dashboard", desc: "Paid, organic, email and CRM performance on one screen, updated live." },
      { name: "Channel intelligence", desc: "Dedicated views for paid, organic, and email and outbound marketing." },
      { name: "Executive reports", desc: "Turn performance into board-ready reports in one click." },
    ],
  },
];

/* ------------------------------------------------------------------ */
/*  Quik Intelligence / Why / Who                                      */
/* ------------------------------------------------------------------ */
export const INTELLIGENCE = {
  eyebrow: "Quik Intelligence",
  title: "AI that goes beyond automation.",
  text: "One AI layer reads across every connected app — and moves your team from information to action.",
  steps: [
    ["Understand", "See what’s happening across the whole business."],
    ["Analyse", "Know what’s driving performance, and why."],
    ["Flag", "Spot risks and opportunities before they hit a report."],
    ["Recommend", "Get the next best move, with the reasoning."],
    ["Act", "Turn the decision into assigned work."],
  ] as [string, string][],
  sources: [
    ["QuikCRM", "Renewal due next week"],
    ["QuikSupport", "3 tickets past SLA"],
    ["QuikProjects", "Onboarding 11 days late"],
  ] as [string, string][],
  output: { app: "Quik Intelligence", text: "Churn risk flagged. Save-play assigned." },
  foot: "No integration project. No analyst. Just the next right action.",
};

export const WHY = {
  eyebrow: "Why Quikit",
  title: "Other suites bundle apps.\nQuikit connects them.",
  text: "Most software adds more tools. Quikit adds intelligence that connects them.",
  pillars: [
    { title: "AI-native", text: "Intelligence built into every app — not bolted on as an add-on.", icon: "spark" },
    { title: "One source of truth", text: "Every team sees the same customer, project and number.", icon: "database" },
    { title: "Cross-app automation", text: "Workflows that run across departments, not inside one tool.", icon: "bolt" },
    { title: "Enterprise-ready", text: "Secure, scalable and built to grow with your business.", icon: "shield" },
  ] as const,
};

export const WHO = {
  eyebrow: "Who it’s for",
  title: "Built to scale with you",
  segments: [
    { kicker: "Growing businesses", title: "Start with one app", text: "Solve your most urgent problem first — no platform migration, no big-bang rollout." },
    { kicker: "SMEs", title: "Replace your tool stack", text: "Consolidate subscriptions into one connected suite, one login and one bill." },
    { kicker: "Enterprises", title: "Govern at scale", text: "One data layer, one permission model and enterprise-grade security across every team." },
  ],
};

/* ------------------------------------------------------------------ */
/*  Pricing / QuikOffice                                               */
/* ------------------------------------------------------------------ */
export const PRICING = {
  eyebrow: "Pricing",
  title: "Pay only for the apps you use.",
  text: "Every Quikit app is priced on its own. Start with one, add more as you grow — and get QuikOffice free with any product.",
  steps: [
    ["Choose", "Pick the app that solves today’s problem."],
    ["Connect", "Bring in your team, data and workflows."],
    ["Expand", "Add apps as you grow — everything is already connected."],
  ] as [string, string][],
  includes: ["Pay only for the apps you use", "QuikOffice apps free with every product", "14-day free trial, no credit card", "Enterprise plans available"],
  models: [
    { name: "Per user", text: "For apps your whole team uses — like QuikProjects, QuikCRM and QuikPeople.", from: "$1", unit: "/ user / month", glow: "" },
    { name: "Per brand", text: "For marketing apps priced by the brands you manage — QuikSocial and QuikSEO.", from: "$49.99", unit: "/ brand / month", glow: "radial-gradient(80% 60% at 90% 0%, rgba(255,255,255,0.16), transparent 70%)" },
    { name: "Flat monthly", text: "For team-wide tools with one simple price — QuikPurchase and QuikInsights.", from: "$19.99", unit: "/ month", glow: "radial-gradient(80% 60% at 90% 0%, rgba(110,215,205,0.28), transparent 70%)" },
  ],
  note: "Prices shown in USD per month. Need a custom or enterprise plan? Talk to sales.",
};

export const OFFICE = {
  eyebrow: "QuikOffice",
  title: "Included free with every product.",
  text: "Docs, sheets, slides, forms and e-signatures, all connected to Quikit.",
  apps: ["docs", "sheets", "presentation", "forms", "signs"],
};

/* ------------------------------------------------------------------ */
/*  Blog / FAQ / CTA / footer                                          */
/* ------------------------------------------------------------------ */
export const FAQ = [
  { q: "What is Quikit?", a: "Quikit is an AI-first suite of connected business apps — for sales, projects, people, finance, marketing and support — built on one shared data layer and one AI." },
  { q: "Do I need to use every app?", a: "No. Every app is priced on its own. Start with the one that solves today’s problem and add more when you need them — they’re already connected." },
  { q: "How is Quikit different from other business software?", a: "Most suites bundle separate tools. Quikit’s apps share one data layer and one AI, so insights and actions work across your whole business, not inside a single tool." },
  { q: "Is there a free trial?", a: "Yes — 14 days on every product, with no credit card and no upfront commitment. QuikOffice (Docs, Sheets, Presentation, Forms and Signs) is included free with any product." },
  { q: "Can Quikit work with the tools we already use?", a: "Yes. Quikit apps support integrations with external tools — such as email, calendars, APIs and SSO — and availability varies by app. Each product page lists what it connects with." },
  { q: "Is Quikit secure and enterprise-ready?", a: "Yes. Quikit is built with enterprise-grade security and access controls, designed to scale across teams and functions." },
  { q: "What does “From $1” mean?", a: "Our per-user apps start at $1 per user per month. Every price on this site is in USD, billed monthly." },
];

export const CTA = {
  title: "Put your business on one system.",
  text: "Start with one app today. Connect the rest as you grow.",
};

export const CONTACT = {
  /** What Quikit actually is, for the demo form's left column. Counts come from the
   *  catalogue above so they cannot drift as products are added. */
  points: [
    `${PRODUCTS.length} connected apps on one platform — ${LIVE_COUNT} ready to use today`,
    "QuikOffice included free with every product",
    "One shared AI layer across sales, projects, people and finance",
    "14-day free trial. No credit card required.",
  ],
  phone: { label: "+91 95222 62841", href: "tel:+919522262841" },
  offices: [
    { region: "India", lines: ["B Zone Business Spaces,", "Indore, MP 452010"] },
    { region: "USA", lines: ["801 C-Bar Ranch Trl,", "Cedar Park, TX 78613"] },
  ],
  socials: [
    { label: "LinkedIn", href: "https://www.linkedin.com/company/quikit/" },
    { label: "YouTube", href: "https://www.youtube.com/@Quikit.official" },
    { label: "Instagram", href: "https://www.instagram.com/quikitofficial" },
    { label: "Facebook", href: "https://www.facebook.com/quikit.official" },
  ],
  tagline: ["Connected business", "Intelligent decisions"],
};

/** The confirmation shown after the demo form is sent — as a dialog, and at /form-submitted. */
export const DEMO_SUCCESS = {
  url: "/form-submitted",
  pill: "Request received",
  /** The sender's first name is appended when the form supplied one. */
  greeting: "Thank you",
  text: "A Quikit specialist will reach out within one business day to set up your walkthrough.",
  points: [
    "A 30-minute walkthrough, built around how your team works today",
    "A plan for the apps that fit — what you start with, and what connects next",
    "Start your 14-day free trial meanwhile. No credit card required.",
  ],
};
